import React, { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useMeeting } from '../../context/meetingContextCore';
import MeetingRecordModal from './MeetingRecordModal';
import MeetingInviteModal from './MeetingInviteModal';
import { copyLink, shareLinkOf } from '../../utils/copyLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark, faClock, faLocationDot, faUsers, faCopy, faPen, faBan, faVideo, faCalendarDay, faClipboardList, faFolderOpen, faLink, faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import api from '../../utils/api';
import RichText from '../RichText';
import Avatar from '../Avatar';
import { RSVP_LABELS, dayText, joinState, rangeText, timeText } from './meetingHelpers';
import '../../styles/meetings.css';

const RSVP_CHOICES = [
    { value: 'accepted', label: 'Accept' },
    { value: 'tentative', label: 'Maybe' },
    { value: 'declined', label: 'Decline' }
];

const HISTORY_LABELS = {
    created: 'Created',
    cancelled: 'Cancelled',
    title: 'Title changed',
    description: 'Details changed',
    startsAt: 'Start time changed',
    endsAt: 'End time changed',
    timezone: 'Timezone changed',
    allDay: 'All-day changed',
    location: 'Location changed',
    visibility: 'Visibility changed',
    settings: 'Settings changed',
    participants: 'Guest list changed'
};

/**
 * One meeting or event in a side panel: the details, who is coming, and what
 * the signed-in person can do about it (answer, edit, cancel).
 */
const MeetingDrawer = ({ meeting, onClose, onChanged, onEdit }) => {
    const [busy, setBusy] = useState(false);
    const [record, setRecord] = useState(null);
    const [showRecord, setShowRecord] = useState(false);
    const [inviting, setInviting] = useState(false);
    const navigate = useNavigate();

    // Somebody let in through the shared link, or invited from inside the meeting, shows up
    // in People without closing and reopening this.
    const changed = useRef(onChanged);
    useEffect(() => { changed.current = onChanged; }, [onChanged]);
    const watching = meeting.kind === 'meeting' && ['scheduled', 'live'].includes(meeting.status);
    useEffect(() => {
        if (!watching) return undefined;
        let alive = true;
        const timer = setInterval(() => {
            api.get(`/meetings/${meeting._id}`).then((res) => { if (alive) changed.current(res.data); }).catch(() => { });
        }, 15000);
        return () => { alive = false; clearInterval(timer); };
    }, [meeting._id, watching]);

    // The record of what happened, for everybody who was there (and the hosts).
    // The server says no to anyone else, in which case there is simply nothing to show.
    const hasStarted = new Date(meeting.startsAt) <= new Date();
    useEffect(() => {
        if (!hasStarted || meeting.kind !== 'meeting') return undefined;
        let alive = true;
        api.get(`/meetings/${meeting._id}/record`)
            .then((res) => { if (alive) setRecord(res.data); })
            .catch(() => { if (alive) setRecord(null); });
        return () => { alive = false; };
    }, [meeting._id, meeting.status, meeting.kind, hasStarted]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const isMeeting = meeting.kind === 'meeting';
    const cancelled = meeting.status === 'cancelled';
    const joining = joinState(meeting);
    // Already in this meeting (it may be minimised): bring it back instead of joining twice.
    const live = useMeeting();
    const liveHere = Boolean(live?.inMeeting) && live.activeId === String(meeting._id);
    const canAnswer = meeting.myRole === 'attendee' || meeting.myRole === 'cohost';

    const answer = async (rsvp) => {
        setBusy(true);
        try {
            const res = await api.post(`/meetings/${meeting._id}/rsvp`, { rsvp });
            onChanged(res.data);
        } catch (err) {
            Swal.fire('Could not save', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setBusy(false);
        }
    };

    // Hosts (the only people the server sends a history to) can send the invitation again.
    const isHostView = Array.isArray(meeting.history);
    const canResend = isHostView && ['scheduled', 'live'].includes(meeting.status);
    const [resending, setResending] = useState('');

    const resend = async (person) => {
        setResending(person._id);
        try {
            const res = await api.post(`/meetings/${meeting._id}/invite/resend`, { participantId: person._id });
            Swal.fire({
                toast: true, position: 'top-end', icon: 'success', showConfirmButton: false, timer: 2200,
                title: res.data.how === 'email' ? `Invitation emailed to ${person.email}` : 'Reminder sent'
            });
        } catch (err) {
            Swal.fire('Not sent', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setResending('');
            // The status line under their name should show how it went.
            try {
                const fresh = await api.get(`/meetings/${meeting._id}`);
                onChanged(fresh.data);
            } catch { /* the list refreshes by itself shortly */ }
        }
    };

    const cancel = async () => {
        const result = await Swal.fire({
            title: `Cancel this ${meeting.kind}?`,
            text: 'It stays on record, marked as cancelled.',
            input: 'text',
            inputPlaceholder: 'Reason (optional)',
            showCancelButton: true,
            confirmButtonText: 'Cancel it',
            cancelButtonText: 'Keep it',
            confirmButtonColor: '#dc2626'
        });
        if (!result.isConfirmed) return;

        setBusy(true);
        try {
            const res = await api.post(`/meetings/${meeting._id}/cancel`, { reason: result.value || '' });
            onChanged(res.data);
        } catch (err) {
            Swal.fire('Could not cancel', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setBusy(false);
        }
    };

    const copyMeetingLink = async () => {
        if (await copyLink(shareLinkOf(meeting))) {
            Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Link copied', showConfirmButton: false, timer: 1400 });
        }
    };

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(meeting.roomCode);
            Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Code copied', showConfirmButton: false, timer: 1400 });
        } catch {
            Swal.fire('Meeting code', meeting.roomCode, 'info');
        }
    };

    return (
        <div className="mt-overlay mt-overlay-side" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <aside className="mt-drawer" role="dialog" aria-modal="true" aria-label={meeting.title}>
                <div className="mt-modal-head">
                    <div className="mt-drawer-title">
                        <span className={`mt-kind ${meeting.kind}`}>
                            <FontAwesomeIcon icon={isMeeting ? faVideo : faCalendarDay} /> {isMeeting ? (meeting.instant ? 'Instant meeting' : 'Meeting') : 'Event'}
                        </span>
                        {cancelled && <span className="mt-kind cancelled">Cancelled</span>}
                        <h3>{meeting.title}</h3>
                    </div>
                    <button type="button" className="mt-icon-btn" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="mt-modal-body">
                    <div className="mt-fact">
                        <FontAwesomeIcon icon={faClock} />
                        <div>
                            <strong>{dayText(meeting.startsAt, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                            <div>{rangeText(meeting)}</div>
                        </div>
                    </div>

                    {meeting.project && (
                        <div className="mt-fact">
                            <FontAwesomeIcon icon={faFolderOpen} />
                            <div>Project: <strong>{meeting.project.name}</strong></div>
                        </div>
                    )}

                    {meeting.location && (
                        <div className="mt-fact"><FontAwesomeIcon icon={faLocationDot} /><div>{meeting.location}</div></div>
                    )}

                    {isMeeting && meeting.roomCode && (
                        <div className="mt-fact">
                            <FontAwesomeIcon icon={faVideo} />
                            <div>
                                <div>Meeting code</div>
                                <button type="button" className="mt-code" onClick={copyCode} title="Copy">
                                    {meeting.roomCode} <FontAwesomeIcon icon={faCopy} />
                                </button>
                                <div className="mt-link-actions">
                                    <button type="button" className="mt-btn small ghost" onClick={copyMeetingLink}>
                                        <FontAwesomeIcon icon={faLink} /> Copy link
                                    </button>
                                    {joining.can && !cancelled && (
                                        <button type="button" className="mt-btn small ghost" onClick={() => setInviting(true)}>
                                            <FontAwesomeIcon icon={faUserPlus} /> Invite
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {isMeeting && !cancelled && (joining.can || joining.opensAt) && (
                        <div className="mt-join-row">
                            {liveHere ? (
                                <button type="button" className="mt-btn primary" onClick={() => { live.expand(); onClose(); }}>
                                    <FontAwesomeIcon icon={faVideo} /> Return to meeting
                                </button>
                            ) : joining.can ? (
                                <button type="button" className="mt-btn primary" onClick={() => navigate(`/meetings/${meeting._id}/room`)}>
                                    <FontAwesomeIcon icon={faVideo} /> Join meeting
                                </button>
                            ) : (
                                <span className="mt-subtle">
                                    You can join from {timeText(joining.opensAt)}
                                    {new Date(joining.opensAt).toDateString() !== new Date().toDateString()
                                        ? ` on ${dayText(joining.opensAt, { day: 'numeric', month: 'short' })}` : ''}.
                                </span>
                            )}
                        </div>
                    )}

                    {meeting.description && <RichText text={meeting.description} className="mt-description" />}

                    {canAnswer && !cancelled && (
                        <div className="mt-rsvp">
                            <span>Your response: <strong>{RSVP_LABELS[meeting.myRsvp] || 'Awaiting reply'}</strong></span>
                            <div className="mt-rsvp-buttons">
                                {RSVP_CHOICES.map(c => (
                                    <button
                                        key={c.value}
                                        type="button"
                                        className={`mt-btn small ${meeting.myRsvp === c.value ? 'primary' : 'ghost'}`}
                                        disabled={busy}
                                        onClick={() => answer(c.value)}
                                    >
                                        {c.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-section">
                        <h4><FontAwesomeIcon icon={faUsers} /> People ({meeting.participants.length})</h4>
                        <ul className="mt-people">
                            {meeting.participants.map(p => (
                                <li key={p._id}>
                                    <Avatar name={p.user?.name || p.name || p.email} profilePic={p.user?.profilePic} className="mt-mini-avatar" />
                                    <div className="mt-person">
                                        <span>{p.user?.name || p.name || p.email}</span>
                                        <small>
                                            {p.role === 'organizer' ? 'Organizer' : p.role === 'cohost' ? 'Co-host' : (p.isExternal ? 'Guest' : 'Invited')}
                                            {p.isExternal && p.email ? ` · ${p.email}` : ''}
                                        </small>
                                        {/* Only the people running the meeting are told how the last email went. */}
                                        {isHostView && p.isExternal && p.lastEmail?.at && (
                                            <small className={`mt-email-status ${p.lastEmail.ok ? 'ok' : 'failed'}`}>
                                                {p.lastEmail.ok
                                                    ? `Email sent ${dayText(p.lastEmail.at, { day: 'numeric', month: 'short' })}, ${timeText(p.lastEmail.at)}`
                                                    : 'The email could not be sent'}
                                            </small>
                                        )}
                                    </div>
                                    <span className={`mt-rsvp-tag ${p.rsvp}`}>{RSVP_LABELS[p.rsvp]}</span>
                                    {canResend && p.role !== 'organizer' && (
                                        <button
                                            type="button"
                                            className="mt-btn small ghost mt-resend"
                                            disabled={resending === p._id}
                                            onClick={() => resend(p)}
                                            title={p.isExternal ? `Email the invitation to ${p.email} again` : 'Send them a reminder in the app'}
                                        >
                                            {resending === p._id ? 'Sending…' : (p.isExternal ? 'Resend' : 'Remind')}
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {meeting.cancelReason && cancelled && (
                        <p className="mt-description">Reason for cancelling: {meeting.cancelReason}</p>
                    )}

                    {record && record.startedAt && (
                        <div className="mt-section mt-record-card">
                            <h4><FontAwesomeIcon icon={faClipboardList} /> Meeting record</h4>
                            <p className="mt-subtle">
                                {record.attended.length} {record.attended.length === 1 ? 'person' : 'people'} joined
                                {' · '}{record.messages.filter((m) => m.kind !== 'notice').length} chat messages
                                {' · '}{record.recording.status === 'none' ? 'not recorded' : 'recorded'}
                            </p>
                            <button type="button" className="mt-btn ghost" onClick={() => setShowRecord(true)}>
                                Open the record
                            </button>
                        </div>
                    )}

                    {meeting.history && meeting.history.length > 0 && (
                        <div className="mt-section">
                            <h4>History</h4>
                            <ul className="mt-history">
                                {[...meeting.history].reverse().map((h, i) => (
                                    <li key={`${h.at}-${i}`}>
                                        <span>{HISTORY_LABELS[h.field] || h.field}</span>
                                        <small>{new Date(h.at).toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</small>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {meeting.canEdit && (
                    <div className="mt-modal-foot">
                        <button type="button" className="mt-btn danger-ghost" disabled={busy} onClick={cancel}>
                            <FontAwesomeIcon icon={faBan} /> Cancel {meeting.kind}
                        </button>
                        <button type="button" className="mt-btn primary" disabled={busy} onClick={() => onEdit(meeting)}>
                            <FontAwesomeIcon icon={faPen} /> Edit
                        </button>
                    </div>
                )}
            </aside>

            {showRecord && record && <MeetingRecordModal record={record} onClose={() => setShowRecord(false)} />}
            {inviting && (
                <MeetingInviteModal
                    meetingId={meeting._id}
                    link={shareLinkOf(meeting)}
                    onClose={() => setInviting(false)}
                    onInvited={() => api.get(`/meetings/${meeting._id}`).then((res) => onChanged(res.data)).catch(() => { })}
                />
            )}
        </div>
    );
};

export default MeetingDrawer;
